import { InvoiceItem, Product } from "@lib/types/Billing";
import { InputNumber, Select, Table, Typography } from "antd";
import AppColumnsTable from "@lib/components/MiniComponents/Table/AppColumnsTable";
import useIntl from "react-intl/lib/src/components/useIntl";
import { itemTotalPrice } from "./utils";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import CoinPriceTextFormat from "../Others/CoinPriceTextFormat";
import { StyledInvoiceItems } from "./index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

const { Column, ColumnGroup } = Table;

type Props = {
  items: InvoiceItem[];
  invoiceProducts: Product[];
  onChangeLineItems: (
    index: number,
    key: keyof InvoiceItem,
    value: string | number,
  ) => void;
  onDeleteLineItem: (index: number) => void;
  loading: boolean;
  open?: boolean;
  productsQuery: UseQueryProps<PaginationType<Product>>;
  isEditMode: boolean;
};

export default function InvoiceItems(props: Props) {
  const {
    items,
    invoiceProducts,
    onChangeLineItems,
    onDeleteLineItem,
    loading,
    productsQuery,
    isEditMode,
  } = props;
  const { messages } = useIntl();

  return (
    <StyledInvoiceItems id="invoice-items">
      <AppColumnsTable<InvoiceItem>
        dataSource={items.map((item) => ({
          ...item,
          key: item.id,
          product: invoiceProducts?.find((product) => product?.id === item.id),
        }))}
        loading={loading}
      >
        <ColumnGroup>
          <Column
            title="#"
            dataIndex="key"
            key="key"
            render={(value, record: InvoiceItem, index) => index + 1}
          />
          <Column
            title={messages["common.product"] as string}
            dataIndex="id"
            key="id"
            render={(value, record: InvoiceItem, index) =>
              isEditMode ? (
                <Select
                  allowClear
                  showSearch
                  loading={productsQuery.isLoading}
                  value={value || ""}
                  placeholder={messages["common.product"] as string}
                  onChange={(e) => {
                    onChangeLineItems(index, "id", e);
                  }}
                  style={{ minWidth: "200px", maxWidth: "300px" }}
                  optionFilterProp="text"
                  options={productsQuery?.data?.data?.data?.map((product) => ({
                    label: product.name,
                    text: product.name,
                    value: product.id,
                  }))}
                />
              ) : (
                <Typography.Text>
                  {record?.product?.name || "-"}
                </Typography.Text>
              )
            }
          />
          <Column
            title={messages["common.unitPrice"] as string}
            dataIndex="unitPrice"
            key="unitPrice"
            render={(unitPrice, record: InvoiceItem, index) =>
              isEditMode ? (
                <InputNumber
                  value={unitPrice || 0}
                  placeholder={messages["common.unitPrice"] as string}
                  onChange={(value) => {
                    onChangeLineItems(index, "unitPrice", value);
                  }}
                  min={0}
                />
              ) : (
                <CoinPriceTextFormat amount={unitPrice} bold />
              )
            }
          />
          <Column
            title={messages["common.quantity"] as string}
            dataIndex="quantity"
            key="quantity"
            render={(quantity, record: InvoiceItem, index) =>
              isEditMode ? (
                <InputNumber
                  value={quantity || 0}
                  placeholder={messages["common.quantity"] as string}
                  onChange={(value) => {
                    onChangeLineItems(index, "quantity", value);
                  }}
                  min={1}
                  max={1000}
                />
              ) : (
                <CoinPriceTextFormat amount={quantity} bold />
              )
            }
          />
          <Column
            title={messages["common.discount"] as string}
            dataIndex="discount"
            key="discount"
            render={(value, record: InvoiceItem, index) =>
              isEditMode ? (
                <div style={{ display: "flex" }}>
                  <InputNumber
                    style={{ width: "100px" }}
                    value={value}
                    onChange={(value) => {
                      onChangeLineItems(index, "discount", value);
                    }}
                    min={0}
                    max={record.unitPrice * record.quantity}
                  />
                </div>
              ) : (
                <CoinPriceTextFormat amount={itemTotalPrice(record)} bold />
              )
            }
          />
          <Column
            title={messages["common.total"] as string}
            dataIndex="total"
            key="total"
            width={"90px"}
            align="right"
            render={(total, record: InvoiceItem) => (
              <CoinPriceTextFormat amount={itemTotalPrice(record)} bold />
            )}
          />
          {isEditMode && (
            <Column
              title={messages["common.delete"] as string}
              dataIndex="total"
              key="total"
              width={"60px"}
              className="no-print"
              render={(total, record: InvoiceItem, index) => (
                <ActionButton
                  type="delete"
                  onClick={() => onDeleteLineItem(index)}
                  itemName="common.invoiceItem"
                  noLabel
                  noBg
                  size="small"
                />
              )}
            />
          )}
        </ColumnGroup>
      </AppColumnsTable>
    </StyledInvoiceItems>
  );
}
